<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import BugReportAttachments from './BugReportAttachments.vue';
import { statusBadgeClass } from './statusBadge';
defineProps({
  report: { type: Object, required: true },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['comment', 'download', 'back']);
const { locale } = useI18n();
const dateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
);
const body = ref('');
const submissionId = ref(crypto.randomUUID());
const files = ref([]);
const clear = () => {
  submissionId.value = crypto.randomUUID();
  body.value = '';
  files.value = [];
};
defineExpose({ clear });
</script>

<template>
  <article class="space-y-4">
    <Button
      :label="$t('BUG_REPORTS.back')"
      variant="ghost"
      @click="emit('back')"
    />
    <h2 class="text-xl font-semibold">{{ report.title }}</h2>
    <p class="break-all">{{ report.protocol }}</p>
    <time :datetime="report.created_at" class="block text-sm text-n-slate-11">
      {{ dateFormatter.format(new Date(report.created_at)) }}
    </time>
    <span
      class="inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold"
      :class="statusBadgeClass(report.status)"
    >
      {{ $t(`BUG_REPORTS.values.${report.status}`) }}
    </span>
    <p v-if="report.evidence_purged_at" class="text-sm text-n-slate-11">
      {{ $t('BUG_REPORTS.evidence_expired') }}
    </p>
    <div
      v-for="field in [
        'description',
        'steps',
        'expected_result',
        'observed_result',
      ]"
      :key="field"
    >
      <template v-if="report[field]">
        <h3 class="font-medium">{{ $t(`BUG_REPORTS.fields.${field}`) }}</h3>
        <p class="whitespace-pre-wrap break-words">
          {{ report[field] }}
        </p>
      </template>
    </div>
    <Button
      v-for="image in report.images"
      :key="image.id"
      :label="image.filename"
      variant="link"
      @click="emit('download', image)"
    />
    <h3 class="font-semibold">{{ $t('BUG_REPORTS.history') }}</h3>
    <p v-for="event in report.events" :key="event.id">
      {{ $t(`BUG_REPORTS.values.${event.action}`) }}
      <time :datetime="event.created_at" class="block text-sm text-n-slate-11">
        {{ dateFormatter.format(new Date(event.created_at)) }}
      </time>
    </p>
    <article
      v-for="comment in report.comments"
      :key="comment.id"
      class="p-4 border border-n-weak rounded-lg"
    >
      <strong>{{
        $t(comment.from_author ? 'BUG_REPORTS.you' : 'BUG_REPORTS.team')
      }}</strong>
      <time
        :datetime="comment.created_at"
        class="block text-sm text-n-slate-11"
      >
        {{ dateFormatter.format(new Date(comment.created_at)) }}
      </time>
      <p class="whitespace-pre-wrap break-words">{{ comment.body }}</p>
      <Button
        v-for="image in comment.images"
        :key="image.id"
        :label="image.filename"
        variant="link"
        @click="emit('download', image)"
      />
    </article>
    <form
      v-if="!['closed', 'resolved'].includes(report.status)"
      class="space-y-3"
      @submit.prevent="emit('comment', body, files, submissionId)"
    >
      <fieldset :disabled="busy" class="space-y-3">
        <label class="block">
          <span>{{ $t('BUG_REPORTS.body') }}</span>
          <textarea v-model="body" required maxlength="10000" class="w-full" />
        </label>
        <BugReportAttachments v-model="files" />
        <Button
          class="!text-n-slate-12 dark:!text-n-slate-1"
          type="submit"
          :label="$t('BUG_REPORTS.reply')"
          :is-loading="busy"
        />
      </fieldset>
    </form>
  </article>
</template>
